/**
 * API Service with Automatic Token Refresh
 *
 * Features:
 * - Automatic silent token refresh on 401
 * - Request queuing during refresh
 * - Consistent token management
 */

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  storeTokens,
} from './tokenStore'

export const API_BASE = import.meta.env.VITE_API_URL || 'https://loveline-api.returned.com'
export const AUTH_BASE = import.meta.env.VITE_AUTH_URL || 'https://identity.returned.com'

function deriveWsBase(httpBase: string): string | null {
  try {
    const u = new URL(httpBase)
    if (u.protocol === 'https:') u.protocol = 'wss:'
    else if (u.protocol === 'http:') u.protocol = 'ws:'
    else return null
    return u.origin
  } catch {
    return null
  }
}

/**
 * WebSocket base URL.
 *
 * Prefer explicit `VITE_API_WS_URL` when provided, otherwise derive from `API_BASE`.
 * This keeps REST + WS in the same environment (prod/staging/local) by default.
 */
export const WS_BASE = (() => {
  const explicit = (import.meta.env.VITE_API_WS_URL || '').trim()
  if (explicit) return explicit.replace(/\/+$/, '')
  return (deriveWsBase(API_BASE) || 'wss://returned.com').replace(/\/+$/, '')
})()

// Refresh state management
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null
export { getAccessToken, getRefreshToken, storeTokens, clearTokens } from './tokenStore'

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    console.log('[API] No refresh token available')
    return null
  }

  try {
    console.log('[API] Refreshing access token...')
    const response = await fetch(`${AUTH_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      console.log('[API] Token refresh failed:', response.status)
      // Refresh failed - clear tokens and redirect to login
      clearTokens()
      return null
    }

    const data = await response.json()
    console.log('[API] Token refreshed successfully')

    // Store new tokens
    storeTokens(data.access_token, data.refresh_token)

    return data.access_token
  } catch (error) {
    console.error('[API] Token refresh error:', error)
    clearTokens()
    return null
  }
}

/**
 * Handle token refresh with request queuing
 */
async function handleTokenRefresh(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = refreshAccessToken()

  try {
    const token = await refreshPromise
    return token
  } finally {
    isRefreshing = false
    refreshPromise = null
  }
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function apiFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let response = await apiRaw(endpoint, options)

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return {} as T
  }

  return JSON.parse(text)
}

function isBodyFormData(body: BodyInit | null | undefined): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

function isBinaryBody(body: BodyInit | null | undefined): boolean {
  if (!body) return false
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true
  if (body instanceof ArrayBuffer) return true
  if (ArrayBuffer.isView(body)) return true
  return false
}

/**
 * Raw response helper with token refresh/retry semantics.
 */
export async function apiRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const base = (!endpoint.startsWith('http') && endpoint.startsWith('/api/auth/'))
    ? AUTH_BASE : API_BASE
  const url = endpoint.startsWith('http') ? endpoint : `${base}${endpoint}`

  const makeRequest = async (authToken: string | null): Promise<Response> => {
    const headers = new Headers(options.headers || {})

    if (authToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${authToken}`)
    }

    // Only default JSON content-type for plain string request bodies.
    if (
      options.body &&
      typeof options.body === 'string' &&
      !headers.has('Content-Type') &&
      !isBodyFormData(options.body) &&
      !isBinaryBody(options.body)
    ) {
      headers.set('Content-Type', 'application/json')
    }

    return fetch(url, {
      ...options,
      headers,
    })
  }

  let response = await makeRequest(getAccessToken())

  if (response.status === 401) {
    console.log('[API] Got 401, attempting token refresh...')
    const newToken = await handleTokenRefresh()
    if (!newToken) {
      throw new AuthError('Session expired. Please log in again.')
    }
    console.log('[API] Retrying request with new token')
    response = await makeRequest(newToken)
  }

  return response
}

export async function apiText(endpoint: string, options: RequestInit = {}): Promise<string> {
  const response = await apiRaw(endpoint, options)
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }
  return response.text()
}

export async function apiBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
  const response = await apiRaw(endpoint, options)
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new ApiError(errorText, response.status)
  }
  return response.blob()
}

/**
 * Custom error classes
 */
export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * Convenience methods
 */
export const api = {
  get: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  put: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  patch: <T = unknown>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      headers:
        body instanceof FormData
          ? options?.headers
          : { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    }),

  delete: <T = unknown>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),

  raw: (endpoint: string, options?: RequestInit) => apiRaw(endpoint, options),
  text: (endpoint: string, options?: RequestInit) => apiText(endpoint, options),
  blob: (endpoint: string, options?: RequestInit) => apiBlob(endpoint, options),
}

export default api
